import type { AccountLedger } from '../api/generated/model'
import { formatDate, formatMoney } from '../lib/money'

/**
 * One account's headline numbers.
 *
 * The minimum balance is given more weight than the closing balance, because the low-water
 * mark is what actually answers "am I going to be fine?" — a healthy end-state tells you
 * nothing if the account went negative in March.
 */
export function AccountCard({
  ledger,
  selected,
  onSelect,
}: {
  ledger: AccountLedger
  selected: boolean
  onSelect: () => void
}) {
  const zeroBreaches = ledger.breaches.filter((b) => b.type === 'ZERO')
  const bufferBreaches = ledger.breaches.filter((b) => b.type === 'BUFFER')

  const status = zeroBreaches.length > 0 ? 'bad' : bufferBreaches.length > 0 ? 'warn' : 'good'

  return (
    <button
      type="button"
      className={`account-card ${selected ? 'is-selected' : ''}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <div className="account-card-head">
        <span className="account-name">{ledger.name}</span>
        <span className={`dot dot-${status}`} aria-hidden="true" />
      </div>

      <div className="account-figure">
        <span className="figure-label">Minimum</span>
        <span className={`figure-value ${status === 'bad' ? 'is-negative' : ''}`}>
          {formatMoney(ledger.minimumBalanceMinor, ledger.currency)}
        </span>
        <span className="figure-note">{formatDate(ledger.minimumBalanceDate)}</span>
      </div>

      <dl className="account-meta">
        <div>
          <dt>Today</dt>
          <dd>{formatMoney(ledger.openingBalanceMinor, ledger.currency)}</dd>
        </div>
        <div>
          <dt>Horizon end</dt>
          <dd>{formatMoney(ledger.closingBalanceMinor, ledger.currency)}</dd>
        </div>
      </dl>

      {zeroBreaches.length > 0 && (
        <p className="account-alert is-bad">
          Overdrawn on {zeroBreaches.length} {zeroBreaches.length === 1 ? 'day' : 'days'}, first{' '}
          {formatDate(zeroBreaches[0].date)}
        </p>
      )}
      {zeroBreaches.length === 0 && bufferBreaches.length > 0 && (
        <p className="account-alert is-warn">
          Below buffer on {bufferBreaches.length} days, first {formatDate(bufferBreaches[0].date)}
        </p>
      )}
    </button>
  )
}
